import { updatePersonalUserOfferMetrics } from "../networking/metrics/updatePersonalUserOfferMetrics";

export class SessionTracker {
  private sessionStartTime: number = Date.now();
  private lastActivityTime: number = Date.now();
  private lastActivityUpdate: number = Date.now(); // Track when we last successfully sent an update
  private inactivityTimeout: number = 30 * 60 * 1000; // 30 minutes in milliseconds
  private updateInterval: number = 60000; // Check for updates every minute
  private inactivityCheckInterval: NodeJS.Timeout | null = null;
  private metricsUpdateInterval: NodeJS.Timeout | null = null;
  private activityTimeout: NodeJS.Timeout | null = null;
  private sessionActive: boolean = true;
  private personalOfferId: string;
  private emitEvent: (eventName: string, data: any) => void;

  constructor(
    personalOfferId: string,
    emitEventCallback: (eventName: string, data: any) => void
  ) {
    this.personalOfferId = personalOfferId;
    this.emitEvent = emitEventCallback;
    this.startTracking();
  }

  private startTracking() {
    // Reset all time tracking
    const now = Date.now();
    this.sessionStartTime = now;
    this.lastActivityTime = now;
    this.lastActivityUpdate = now;
    this.sessionActive = true;

    // Set up activity tracking
    this.setupActivityTracking();

    // Set up periodic metrics updates
    this.metricsUpdateInterval = setInterval(() => {
      this.updateMetrics();
    }, this.updateInterval);

    // Set up inactivity check
    this.inactivityCheckInterval = setInterval(() => {
      this.checkInactivity();
    }, this.updateInterval);

    // Set up page unload handler
    window.addEventListener("beforeunload", () => {
      this.endSession("page_unload");
    });
  }

  private async updateMetrics() {
    if (!this.sessionActive) return;

    const now = Date.now();
    const elapsedSeconds = Math.floor((now - this.lastActivityUpdate) / 1000);

    // Only update if we have accumulated at least a second
    if (elapsedSeconds > 0) {
      try {
        // Use emitEvent instead of direct API call
        this.emitEvent("SESSION_LENGTH", {
          metricName: "sessionLengthSeconds",
          value: elapsedSeconds,
        });

        // Only update our last update time if the emit succeeded
        this.lastActivityUpdate = now;
      } catch (error) {
        console.error("Failed to update metrics:", error);
        // Don't update lastActivityUpdate so we'll try to send this time again
      }
    }
  }

  private setupActivityTracking() {
    const activityEvents = [
      "mousedown",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      const now = Date.now();
      if (!this.sessionActive) {
        // Session was inactive, restart it
        this.sessionStartTime = now;
        this.sessionActive = true;
      }
      this.lastActivityTime = now;

      // Debounce the metrics update
      if (this.activityTimeout) {
        clearTimeout(this.activityTimeout);
      }
      this.activityTimeout = setTimeout(() => {
        this.updateMetrics();
      }, 1000); // Wait 1 second after last activity before trying to update
    };

    activityEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity, { passive: true });
    });
  }

  private checkInactivity() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    if (timeSinceLastActivity >= this.inactivityTimeout && this.sessionActive) {
      this.endSession("inactivity");
    }
  }

  private async endSession(reason: "inactivity" | "page_unload") {
    if (!this.sessionActive) return;

    this.sessionActive = false;

    // Clear all intervals and timeouts
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    }
    if (this.metricsUpdateInterval) {
      clearInterval(this.metricsUpdateInterval);
      this.metricsUpdateInterval = null;
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    // Send final update for any remaining time since last successful update
    const remainingSeconds = Math.floor(
      (Date.now() - this.lastActivityUpdate) / 1000
    );

    if (remainingSeconds > 0) {
      try {
        // Use emitEvent instead of direct API call
        this.emitEvent("SESSION_LENGTH", {
          metricName: "sessionLengthSeconds",
          value: remainingSeconds,
        });
      } catch (error) {
        console.error("Failed to update final session metrics:", error);
      }
    }

    // Log session end
    console.log(`SessionTracker: Ending session due to ${reason}`, {
      sessionLength: Math.floor((Date.now() - this.sessionStartTime) / 1000),
      startTime: new Date(this.sessionStartTime).toISOString(),
      endTime: new Date().toISOString(),
    });
  }

  public cleanup() {
    // Method to clean up all listeners and intervals
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    }
    if (this.metricsUpdateInterval) {
      clearInterval(this.metricsUpdateInterval);
      this.metricsUpdateInterval = null;
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    // Remove all event listeners
    const activityEvents = [
      "mousedown",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((eventName) => {
      document.removeEventListener(eventName, () => {});
    });

    window.removeEventListener("beforeunload", () => {});
  }
}
